'use client';

import s from 'Workadabra/app/[lang]/home/sections/footer/styles.module.css';
import { socialAccounts } from 'Workadabra/shared/social';
import { landingFooterColCompany, landingFooterColLegal, landingFooterColProduct, landingFooterColTrust, landingFooterCopyright, landingFooterTagline } from 'Workadabra/translations';
import { BomdisoftBacklink, Brand } from '@bomdisoft/components';
import { hubBacklinkHref } from '@bomdisoft/seo';
import { getLanguageFromLocale } from '@bomdisoft/translations';
import { useParams } from 'next/navigation';

const FacebookIcon = () => (
  <svg aria-hidden='true' fill='currentColor' height={16} viewBox='0 0 24 24' width={16} xmlns='http://www.w3.org/2000/svg'>
    <path d='M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.99 22 12z' />
  </svg>
);

const InstagramIcon = () => (
  <svg aria-hidden='true' fill='currentColor' height={16} viewBox='0 0 24 24' width={16} xmlns='http://www.w3.org/2000/svg'>
    <path d='M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z' />
  </svg>
);

const LinkedInIcon = () => (
  <svg aria-hidden='true' fill='currentColor' height={16} viewBox='0 0 24 24' width={16} xmlns='http://www.w3.org/2000/svg'>
    <path d='M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' />
  </svg>
);

const XIcon = () => (
  <svg aria-hidden='true' fill='currentColor' height={16} viewBox='0 0 24 24' width={16} xmlns='http://www.w3.org/2000/svg'>
    <path d='M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' />
  </svg>
);

export const Footer = () => {
  const params = useParams();
  const langPrefix = typeof params?.lang === 'string' ? params.lang : 'en';
  const language = getLanguageFromLocale(langPrefix);

  const productLinks = [
    { href: `/${langPrefix}#how-it-works`, label: 'How it works' },
    { href: `/${langPrefix}#services`, label: 'Services' },
    { href: `/${langPrefix}#earn`, label: 'Earn' },
    { href: process.env.NEXT_PUBLIC_APP_URL ?? '#', label: 'Get the app' },
  ];

  const companyLinks = [
    { href: `/${langPrefix}/about`, label: 'About' },
    { href: `/${langPrefix}/careers`, label: 'Careers' },
    { href: `/${langPrefix}/community`, label: 'Community' },
    { href: `/${langPrefix}/contact`, label: 'Contact' },
  ];

  const trustLinks = [
    { href: `/${langPrefix}/help-center`, label: 'Help center' },
    { href: `/${langPrefix}/security`, label: 'Security' },
    { href: `/${langPrefix}/gdpr`, label: 'GDPR' },
    { href: `/${langPrefix}/cookie-policy`, label: 'Cookie policy' },
  ];

  const legalLinks = [
    { href: `/${langPrefix}/privacy-policy`, label: 'Privacy policy' },
    { href: `/${langPrefix}/terms-of-service`, label: 'Terms of service' },
    { href: `/${langPrefix}/data-deletion`, label: 'Data deletion' },
  ];

  const columns = [
    { header: landingFooterColProduct[language], links: productLinks, testId: 'footer-col-product' },
    { header: landingFooterColCompany[language], links: companyLinks, testId: 'footer-col-company' },
    { header: landingFooterColTrust[language], links: trustLinks, testId: 'footer-col-trust' },
    { header: landingFooterColLegal[language], links: legalLinks, testId: 'footer-col-legal' },
  ];

  return (
    <footer className={s.root} data-testid='footer'>
      <div className={s.content}>
        <div className={s.top}>
          <div className={s.brandCol}>
            <Brand gap={0.15} logoHeight={18} logoWidth={30} size={24} testId='footer-brand' wordmarkSuffix='orkadabra' />
            <p className={s.tagline} data-testid='footer-tagline'>
              {landingFooterTagline[language]}
            </p>
            <div className={s.socialRow}>
              <a aria-label='Facebook' className={s.socialIcon} href={socialAccounts.facebook} rel='noopener noreferrer' target='_blank'>
                <FacebookIcon />
              </a>
              <a aria-label='Instagram' className={s.socialIcon} href={socialAccounts.instagram} rel='noopener noreferrer' target='_blank'>
                <InstagramIcon />
              </a>
              <a aria-label='LinkedIn' className={s.socialIcon} href={socialAccounts.linkedin} rel='noopener noreferrer' target='_blank'>
                <LinkedInIcon />
              </a>
              <a aria-label='X' className={s.socialIcon} href={socialAccounts.x} rel='noopener noreferrer' target='_blank'>
                <XIcon />
              </a>
            </div>
          </div>

          {columns.map(column => (
            <div className={s.col} key={column.header}>
              <div className={s.colHeader} data-testid={column.testId}>
                {column.header}
              </div>
              <div className={s.colLinks}>
                {column.links.map(link => (
                  <a className={s.colLink} href={link.href} key={link.href}>
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className={s.bottomStrip}>
          <span className={s.small} data-testid='footer-copyright'>
            {landingFooterCopyright[language]}
          </span>
          <BomdisoftBacklink href={hubBacklinkHref('workadabra', langPrefix)} />
          <div className={s.bottomStripContact}>
            {socialAccounts.mobile && (
              <a className={`${s.colLink} ${s.small}`} href={`tel:${socialAccounts.mobile.replace(/\s/g, '')}`}>
                {socialAccounts.mobile}
              </a>
            )}
            {socialAccounts.email && (
              <a className={`${s.colLink} ${s.small}`} href={`mailto:${socialAccounts.email}`}>
                {socialAccounts.email}
              </a>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};
